import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useRequests } from '../../requests/useRequests'
import { useDispatch } from 'react-redux'
import { Product } from '../../types/product'
import { Brand } from '../../types/brand'
import ButtonLink from '../Buttons/ButtonLink'

const ChevronLeftIcon = () => (
  <svg className='tw-w-5 tw-h-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z' clipRule='evenodd' />
  </svg>
)

const ChevronRightIcon = () => (
  <svg className='tw-w-5 tw-h-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z' clipRule='evenodd' />
  </svg>
)

const ExclamationTriangleIcon = () => (
  <svg className='tw-h-5 tw-w-5 tw-text-red-400' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor' aria-hidden='true'>
    <path fillRule='evenodd' d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z' clipRule='evenodd' />
  </svg>
)

const ShoppingBagIcon = () => (
  <svg className='tw-mx-auto tw-h-12 tw-w-12 tw-text-slate-400' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth='1.5' stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z' />
  </svg>
)

const ProductCard: React.FC<{ product: Product, onProductClick: (id: number | string) => void }> = ({ product, onProductClick }) => {
  const { name, imagePresignedUrl, image, id, brand, category } = product
  const imageUrl = imagePresignedUrl || image || (product.image && `/api/uploads/image/${product.image}`)

  return (
    <div
      onClick={() => onProductClick(id)}
      className='tw-relative tw-aspect-[4/3] tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-group'
    >
      {/* Zdjęcie produktu */}
      <div className='tw-absolute tw-inset-0'>
        {imageUrl
          ? (
            <img
              src={imageUrl}
              alt={name}
              className='tw-w-full tw-h-full tw-object-cover'
            />
            )
          : (
            <div className='tw-w-full tw-h-full tw-bg-slate-200 tw-flex tw-items-center tw-justify-center'>
              <span className='tw-text-slate-400 tw-text-sm'>Brak obrazka</span>
            </div>
            )}
      </div>

      {/* Nakładka z informacjami */}
      <div className='tw-absolute tw-inset-0 tw-bg-black/70 tw-flex tw-items-center tw-justify-center tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100'>
        <div className='tw-text-center tw-p-4'>
          <h3 className='tw-text-white tw-text-lg tw-font-semibold tw-mb-2'>
            {name}
          </h3>
          {brand?.name && (
            <p className='tw-text-white tw-text-sm tw-mb-1'>
              {brand.name}
            </p>
          )}
          {category && (
            <p className='tw-text-white tw-text-sm'>
              {category}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

const FeaturedProducts: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requests = useRequests()
  const [loading, setLoading] = useState<boolean>(true)
  const [products, setProducts] = useState<Product[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [error, setError] = useState<string | null>(null)
  const [topBrands, setTopBrands] = useState<Brand[]>([])
  // Dodane stany do paginacji top marek
  const [topBrandsPage, setTopBrandsPage] = useState(0)
  const brandsPerPage = 5
  const totalTopBrandsPages = Math.ceil(topBrands.length / brandsPerPage)
  const autoSlideInterval = 10000 // 10 sekund
  // Ref do timera
  const autoSlideRef = React.useRef<NodeJS.Timeout | null>(null)

  // Automatyczne przewijanie stron top marek
  useEffect(() => {
    if (totalTopBrandsPages <= 1) return
    if (autoSlideRef.current) clearInterval(autoSlideRef.current)
    autoSlideRef.current = setInterval(() => {
      setTopBrandsPage((prev) => (prev + 1) % totalTopBrandsPages)
    }, autoSlideInterval)
    return () => {
      if (autoSlideRef.current) clearInterval(autoSlideRef.current)
    }
  }, [totalTopBrandsPages])

  // Funkcje do obsługi przycisków
  const handleTopBrandsPrev = () => {
    setTopBrandsPage((prev) => (prev - 1 + totalTopBrandsPages) % totalTopBrandsPages)
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current)
      autoSlideRef.current = setInterval(() => {
        setTopBrandsPage((prev) => (prev + 1) % totalTopBrandsPages)
      }, autoSlideInterval)
    }
  }
  const handleTopBrandsNext = () => {
    setTopBrandsPage((prev) => (prev + 1) % totalTopBrandsPages)
    if (autoSlideRef.current) {
      clearInterval(autoSlideRef.current)
      autoSlideRef.current = setInterval(() => {
        setTopBrandsPage((prev) => (prev + 1) % totalTopBrandsPages)
      }, autoSlideInterval)
    }
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      requests.getProducts({ limit: 12, sort: 'priority:desc' }),
      requests.getBrands({ limit: 100 }),
      requests.getBrands({ limit: 15, sort: 'priority:desc' })
    ])
      .then(([productsResponse, brandsResponse, topBrandsResponse]) => {
        setProducts(productsResponse.data.data || [])

        const allBrands = brandsResponse.data.data || []
        const shuffled = [...allBrands].sort(() => 0.5 - Math.random())
        setBrands(shuffled.slice(0, 4))

        setTopBrands(topBrandsResponse.data.data || [])
        setLoading(false)
      })
      .catch(error => {
        console.error('Błąd podczas pobierania danych:', error)
        setError('Wystąpił błąd podczas pobierania danych. Spróbuj odświeżyć stronę.')
        setLoading(false)
      })
  }, [requests, dispatch])

  const handleProductClick = (id: number | string) => {
    navigate(`/products/${id}?add=true`)
  }

  const handleBrandClick = (id: number) => {
    navigate(`/brands/${id}`)
  }

  if (loading) {
    return (
      <div className='loading-container'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-3'>Wczytywanie produktów...</p>
      </div>
    )
  }

  if (error) {
    return <></>
  }

  if (products.length === 0 && brands.length === 0) {
    return <></>
  }

  return (
    <section className='tw-bg-white tw-pt-24 md:tw-pt-28 xl:tw-pt-32 tw-pb-2 md:tw-pb-2 xl:tw-pb-2'>
      <div className='tw-container xl:tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8'>
        {/* TOP BRANDS SECTION */}
        {topBrands.length > 0 && (
          <div className='tw-mb-12'>
            <div className='tw-flex tw-items-center tw-justify-between tw-mb-6'>
              <div className='tw-flex tw-items-center tw-gap-x-3 md:tw-gap-x-4'>
                <div className='tw-h-1 tw-w-12 md:tw-w-16 tw-bg-green' />
                <h2 className='tw-text-sm tw-font-semibold tw-uppercase tw-text-green tw-tracking-wide'>Top marki</h2>
              </div>
              {/* Przyciski i kropki */}
              <div className='tw-flex tw-items-center tw-gap-x-4'>
                <button onClick={handleTopBrandsPrev} className='tw-p-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm hover:tw-bg-slate-100 tw-transition'>
                  <ChevronLeftIcon />
                </button>
                <div className='tw-flex tw-items-center tw-gap-x-1'>
                  {Array.from({ length: totalTopBrandsPages }).map((_, idx) => (
                    <span
                      key={idx}
                      className={`tw-inline-block tw-w-3 tw-h-3 tw-rounded-full ${idx === topBrandsPage ? 'tw-bg-green tw-border tw-border-green' : 'tw-bg-slate-300'} tw-transition`}
                    />
                  ))}
                </div>
                <button onClick={handleTopBrandsNext} className='tw-p-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-shadow-sm hover:tw-bg-slate-100 tw-transition'>
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
            <div className='tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-6'>
              {topBrands.slice(topBrandsPage * brandsPerPage, (topBrandsPage + 1) * brandsPerPage).map((brand) => (
                <div
                  key={brand.id}
                  onClick={() => handleBrandClick(brand.id)}
                  className='tw-relative tw-aspect-square tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-group tw-shadow hover:tw-shadow-lg tw-transition-shadow'
                >
                  <div className='tw-absolute tw-inset-0'>
                    {brand.image
                      ? (
                        <img
                          src={brand.image}
                          alt={brand.name}
                          className='tw-w-full tw-h-full tw-object-cover'
                        />
                      )
                      : (
                        <div className='tw-w-full tw-h-full tw-bg-slate-200 tw-flex tw-items-center tw-justify-center'>
                          <span className='tw-text-slate-400 tw-text-sm'>Brak obrazka</span>
                        </div>
                      )}
                  </div>
                  <div className='tw-absolute tw-inset-0 tw-bg-black/60 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center'>
                    <h3 className='tw-text-white tw-text-lg tw-font-semibold tw-text-center tw-px-2'>
                      {brand.name}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-between sm:tw-items-end tw-mb-8 lg:tw-mb-12'>
          <div className='tw-max-w-xl'>
            <div className='tw-flex tw-items-center tw-gap-x-3 md:tw-gap-x-4'>
              <div className='tw-h-1 tw-w-12 md:tw-w-16 tw-bg-green' />
              <h2 className='tw-text-sm tw-font-semibold tw-uppercase tw-text-green tw-tracking-wide'>Polecane produkty</h2>
            </div>
            <h3 className='tw-text-base md:tw-text-lg xl:tw-text-xl tw-font-semibold tw-text-teal tw-text-center'>
              Odkryj najlepsze produkty w naszym katalogu
            </h3>
          </div>
        </div>

        <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6 tw-pb-8'>
          {products.map((product) => (
            <div key={product.id} className='tw-h-auto'>
              <ProductCard product={product} onProductClick={handleProductClick} />
            </div>
          ))}
        </div>

        <div className='tw-mt-8 tw-text-center'>
          <ButtonLink
            to='/products'
            onClick={() => navigate('/products')}
            className='tw-inline-flex tw-items-center'
          >
            Zobacz wszystkie produkty
            <svg className='tw-w-5 tw-h-5 tw-ml-2 -tw-mr-1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
              <path fillRule='evenodd' d='M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z' clipRule='evenodd' />
            </svg>
          </ButtonLink>
        </div>

        {/* Sekcja z markami */}
        {brands.length > 0 && (
          <div className='tw-mt-24 md:tw-mt-28 xl:tw-mt-32'>
            <div className='tw-flex tw-items-center tw-gap-x-3 md:tw-gap-x-4 tw-mb-8'>
              <div className='tw-h-1 tw-w-12 md:tw-w-16 tw-bg-green' />
              <h2 className='tw-text-sm tw-font-semibold tw-uppercase tw-text-green tw-tracking-wide tw-text-center'>Rekomendowani wykonawcy</h2>
            </div>
            <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6'>
              {brands.map((brand) => (
                <div
                  key={brand.id}
                  onClick={() => handleBrandClick(brand.id)}
                  className='tw-relative tw-aspect-[4/3] tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-group'
                >
                  <div className='tw-absolute tw-inset-0'>
                    {brand.image
                        ? (
                            <img
                                src={brand.image}
                                alt={brand.name}
                                className='tw-w-full tw-h-full tw-object-cover'
                              />
                          )
                        : (
                            <div className='tw-w-full tw-h-full tw-bg-slate-200 tw-flex tw-items-center tw-justify-center'>
                                <span className='tw-text-slate-400 tw-text-sm'>Brak obrazka</span>
                              </div>
                          )}
                  </div>
                  <div className='tw-absolute tw-inset-0 tw-bg-black/50 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center'>
                    <h3 className='tw-text-white tw-text-lg tw-font-semibold'>
                        {brand.name}
                      </h3>
                  </div>
                </div>
              ))}
            </div>
            <div className='tw-mt-8 tw-text-center'>
              <ButtonLink
                to='/brands'
                onClick={() => navigate('/brands')}
                className='tw-inline-flex tw-items-center'
              >
                Zobacz wszystkie marki
                <svg className='tw-w-5 tw-h-5 tw-ml-2 -tw-mr-1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
                  <path fillRule='evenodd' d='M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z' clipRule='evenodd' />
                </svg>
              </ButtonLink>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default FeaturedProducts
